import { computed, ref, Ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import useToast from "@/ui/composables/useToast";
import { t } from "@/plugins/i18n";
import {
	FolderNode,
	isRootNode,
	isSharedWithMeNode,
	ResourceTreeCategory,
} from "./types";
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

/**
 * The folder tree and its create/edit/delete flows.
 *
 * Deliberately knows nothing about files, selection or layout, so both browser
 * presentations can share it without one constraining the other. Callers own
 * which folder is "current" - this only owns the tree itself.
 */
export const useFolderTree = (options: {
	resourceTreeCategory: Ref<ResourceTreeCategory>;
}) => {
	const { resourceTreeCategory } = options;

	const folders: Ref<ResourceTreeNodeDO[]> = ref([]);
	const isLoadingFolders = ref(true);
	const folderToEdit: Ref<ResourceTreeNodeDO | null> = ref(null);
	const isManageFolderDialogVisible = ref(false);

	const nestFolders = (
		items: ResourceTreeNodeDO[],
		parentId: number | null = null
	): FolderNode[] =>
		items
			.filter((item) =>
				parentId === null
					? item.parent_id === -1 || item.parent_id === null
					: item.parent_id === parentId
			)
			.map((item) => ({
				...item,
				disabled: false,
				// "Shared with me" is a flat bucket, so don't recurse into it.
				children: isSharedWithMeNode(item) ? [] : nestFolders(items, item.id),
			}));

	const nestedFolders = computed(() => nestFolders(folders.value));

	const topLevelFolders = computed(() =>
		folders.value.filter(
			(folder) => folder.parent_id === -1 || folder.parent_id === null
		)
	);

	const childFoldersOf = (
		node: ResourceTreeNodeDO | null
	): ResourceTreeNodeDO[] => {
		if (!node || isSharedWithMeNode(node)) {
			return [];
		}

		return folders.value.filter((folder) => folder.parent_id === node.id);
	};

	const rootFolder = computed(() => folders.value.find(isRootNode) ?? null);

	/**
	 * The folder tree loads asynchronously while "Save new" is already clickable,
	 * so keep the in-flight request around: creating a file has to wait for it,
	 * otherwise it posts an undefined resource_tree_node_id and the API 404s.
	 */
	let foldersRequest: Promise<void> | null = null;

	/**
	 * Failure has to end the loading state and say so. Silence here reads as an
	 * empty tree, which is indistinguishable from a user who has no folders -
	 * and leaves `whenFoldersLoaded` pending, which is what "Save new" awaits.
	 */
	const getFolders = (): Promise<void> => {
		isLoadingFolders.value = true;

		foldersRequest = useApiClient("resourceTreeNodes")
			.getResourceTreeNodes(resourceTreeCategory.value)
			.then((response) => {
				if (response.isOk()) {
					folders.value = response.value;

					return;
				}

				useToast().error(t("fileBrowser.foldersLoadFailed"), { timeout: 2000 });
			})
			.catch(() => {
				useToast().error(t("fileBrowser.foldersLoadFailed"), { timeout: 2000 });
			})
			.finally(() => {
				isLoadingFolders.value = false;
			});

		return foldersRequest;
	};

	/** Await before reading any folder id. See the note on `foldersRequest`. */
	const whenFoldersLoaded = (): Promise<void> =>
		foldersRequest ?? Promise.resolve();

	/**
	 * The ancestor chain from the top-level node down to `node`, inclusive.
	 *
	 * One helper covers every way a folder gets opened - a tree click, drilling
	 * into a row, or a breadcrumb click - because each of them wants the whole
	 * chain, not an append. Guards against a cycle in `parent_id`.
	 */
	const pathTo = (node: ResourceTreeNodeDO): ResourceTreeNodeDO[] => {
		const chain: ResourceTreeNodeDO[] = [];
		const seen = new Set<number>();
		let current: ResourceTreeNodeDO | undefined = node;

		while (current) {
			chain.unshift(current);

			const parentId = current.parent_id;

			if (parentId === null || parentId === -1 || seen.has(parentId)) {
				break;
			}

			seen.add(parentId);
			current = folders.value.find((folder) => folder.id === parentId);
		}

		return chain;
	};

	/** "Root / Marketing" for a folder id; empty when the id is unknown. */
	const pathLabelFor = (folderId: number | null): string => {
		if (folderId === null) {
			return "";
		}

		const node = folders.value.find((folder) => folder.id === folderId);

		if (!node) {
			return "";
		}

		return pathTo(node)
			.map((ancestor) => ancestor.name)
			.join(" / ");
	};

	/**
	 * @param parentFolderId Nest the new folder under this one. The endpoint
	 * already parents a new node to Root, so this only overrides that.
	 */
	const createNewFolder = async (parentFolderId?: number): Promise<void> => {
		const response = await useApiClient(
			"resourceTreeNodes"
		).getNewResourceTreeNodes(resourceTreeCategory.value);

		if (response.isOk()) {
			folderToEdit.value = response.value;
			folderToEdit.value.parent_id =
				parentFolderId ?? folderToEdit.value.parent_id;
			isManageFolderDialogVisible.value = true;
		}
	};

	const editFolder = (folder: ResourceTreeNodeDO): void => {
		folderToEdit.value = folder;
		isManageFolderDialogVisible.value = true;
	};

	/** @returns whether the folder was actually deleted, not merely asked about. */
	const removeFolder = async (node: ResourceTreeNodeDO): Promise<boolean> => {
		const confirm = await useDialog().confirm({
			title: t("fileBrowser.delete.folderTitle"),
			message: t("fileBrowser.delete.folderMessage", { name: node.name }),
			isDelete: true,
		});

		if (!confirm) return false;

		const response = await toastApiResult(
			useApiClient("resourceTreeNodes").removeResourceTreeNode(node)
		);

		if (!response.isOk()) {
			return false;
		}

		getFolders();

		return true;
	};

	// Closing the manage dialog re-reads the tree, whether it created, renamed
	// or moved a folder.
	watch(isManageFolderDialogVisible, (isVisible) => {
		if (!isVisible) {
			getFolders();
		}
	});

	return {
		folders,
		isLoadingFolders,
		nestedFolders,
		topLevelFolders,
		childFoldersOf,
		rootFolder,
		pathTo,
		pathLabelFor,
		getFolders,
		whenFoldersLoaded,
		folderToEdit,
		isManageFolderDialogVisible,
		createNewFolder,
		editFolder,
		removeFolder,
	};
};
